<script setup>
/**
 * FormControlModulestructureCategoryComponentId
 * Reusable layout selector filtering registered dynamic system components.
 * Restricts target structural selections based on custom contextual routing parameters
 * and auto-hides itself on mount if the current relationship schema type is invalid.
 */
import {computed, onMounted} from 'vue'
import {useRoute} from 'vue-router'
import {useComponentsStore} from '@/stores/components'
import FormControlTypeSelect from '@/common/form-control-type/FormControlTypeSelect.vue'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
})

const route = useRoute()

// Pinia Stores
const componentsStore = useComponentsStore()

// --- Computed ---

/**
 * Dynamically aggregates, filters, and alphabetizes usable systemic component items
 * based on operational page states or hardcoded table exemptions.
 */
const options = computed(() => {
    let items = componentsStore.items || []

    // Evaluate route-specific visualization boundaries
    const isFieldsView = route.name === 'componentFields'
    const isContentTypesTheme = route.name === 'themesEditor' && route.query?.section === 'content_types'

    if (isFieldsView || isContentTypesTheme) {
        items = items.filter(
            o => o.listing
                || o.categoryList
                // Retain explicit bypass rule targeting specialized custom structural collections
                || o.tableName === 'm_registrants_registrants'
        )
    }

    // Map into standard picklist objects
    const mappedOptions = items.map(o => ({
        value: o.id,
        text: o.title
    }))

    // Return final collection ordered alphabetically by display text
    return mappedOptions.sort((a, b) => (a.text > b.text ? 1 : -1))
})

// --- Lifecycle ---

onMounted(() => {
    /**
     * Instantly enforce layout hidden parameters if mounted against fields
     * failing standard structural database relationship requirements.
     */
    const currentType = props.currentData?.type
    if (currentType !== 'relationshipOneToMany' && currentType !== 'relationshipManyToMany') {
        if (props.field) {
            props.field.hidden = true
        }
    }
})
</script>

<template>
    <FormControlTypeSelect
        v-if="options && options.length"
        :current-data="currentData"
        :field="field"
        :form-id="formId"
        :options="options"
        :required="field.required"
    />
</template>